import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClubCrest from "../common/ClubCrest";
import BottomSheet from "../common/BottomSheet";
import { clubService } from "../../services/api";

// ClubCard — compact club identity card (design frame 6c). Opens on click of a
// club crest / club name from the person-first inbox (header club line and
// inbox row second line). Desktop renders a positioned popover; below the md
// breakpoint the same card content presents as a bottom sheet (6e "card stays"
// ruling — same card verbatim, no copy differences).
//
// Data strategy (two layers): the inbox rows/header stay fetch-free (club_name /
// club_wyscout_id come stamped on channel data). This popover, on open, lazily
// fetches the FULL club profile via the existing public-club endpoint, then
// merges logo / country / league / city over the sparse channel data. A light
// skeleton shows while loading; on error we keep the sparse render — the card
// never breaks.
//
// ID SPACES — do not conflate. The public-club endpoint and the /search/club
// page are keyed on the WYSCOUT team id (`club_wyscout_id`). The channel data
// ALSO carries `club_id`, which is the platform users.Club PK. Both are small
// integers and their ranges overlap, so feeding club_id to the Wyscout route
// does not 404 — it silently returns a DIFFERENT club (a row stamped by
// "QA-FS FC" rendered as "ONS Sneek · Netherlands"). There is deliberately NO
// fallback from one to the other: when the Wyscout id is absent (lite or
// unmatched club, or a channel stamped before the backend carried the key) the
// card simply stays on its sparse, correct channel data and hides the profile
// link. Correct-and-less beats complete-and-wrong.

const CARD_WIDTH = 330;
const GAP = 8;

const ClubCard = ({ club = {}, contactPerson = null, triggerRect, onClose }) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(false);

  // Below md the card is a bottom sheet, not a positioned popover. Tracked live
  // so a rotate/resize across the breakpoint swaps presentation.
  const [isMobile, setIsMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 767px)").matches,
  );
  useEffect(() => {
    const mql = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  // The Wyscout team id — the ONLY id this card may look up with (see the ID
  // SPACES note above). `club.id` (platform PK) is deliberately not consulted.
  const wyscoutId = club.wyscoutId ?? null;

  // Lazily pull the full club profile once, on open. Frontend-only — reuses the
  // existing club-profile service; never edits api.js. Errors are swallowed so
  // the card degrades to the sparse channel-data render.
  useEffect(() => {
    if (wyscoutId == null) return undefined;
    let cancelled = false;
    setLoading(true);
    clubService
      .getClubPublicProfile(wyscoutId)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) {
          setFetched(res.data);
        }
      })
      .catch(() => {
        /* keep sparse render */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [wyscoutId]);

  // Merge fetched club detail over the sparse channel data (fetched wins). The
  // sparse layer arrives on channel users as club_name/club_league/club_logo/
  // club_country (legacy name/logoUrl/country/league/city props still honored).
  const fetchedClub = fetched?.club || null;
  const fetchedCompetition = fetched?.competition || null;
  const clubName = fetchedClub?.name || club.name || null;
  const logoUrl = fetchedClub?.image_url || club.club_logo || club.logoUrl || null;
  const country =
    fetchedClub?.country || club.club_country || club.country || null;
  const league =
    fetchedCompetition?.name || club.club_league || club.league || null;
  const city = fetchedClub?.city || club.city || null;

  // Position the popover under the trigger, clamped to the viewport. Re-runs
  // when the fetch changes the card height so the clamp stays correct.
  useLayoutEffect(() => {
    if (!triggerRect) return;
    const cardHeight = cardRef.current?.offsetHeight || 200;
    let left = triggerRect.left;
    let top = triggerRect.bottom + GAP;

    if (left + CARD_WIDTH > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - CARD_WIDTH - 8);
    }
    if (top + cardHeight > window.innerHeight - 8) {
      top = Math.max(8, triggerRect.top - cardHeight - GAP);
    }
    setPos({ top, left });
  }, [triggerRect, loading, fetched]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const metaLine = [country, league].filter(Boolean).join(" · ");
  // Skeleton only while the fetch is in flight AND we have nothing to show yet —
  // once any real meta exists we render it immediately.
  const showMetaSkeleton = loading && !metaLine && !city;

  // /search/club/:id is the same Wyscout-keyed profile the endpoint above
  // serves, so it takes the same id. Without one there is nothing to open.
  const canViewProfile = wyscoutId != null;
  const handleViewProfile = () => {
    if (canViewProfile) {
      navigate(`/search/club/${wyscoutId}`);
    }
    onClose?.();
  };

  // Shared card content. On mobile the sheet chrome carries the club name in its
  // title, so the inline name line is desktop-only to avoid repeating it.
  const renderCardBody = (inSheet) => (
    <>
        <div className="flex items-center" style={{ gap: 14 }}>
          <ClubCrest name={clubName} logoUrl={logoUrl} variant="card" />
          <div className="flex flex-col" style={{ gap: 3, minWidth: 0 }}>
            {!inSheet && (
            <span
              className="font-bold truncate"
              style={{ fontSize: 16, letterSpacing: "-0.01em" }}
              title={clubName}
            >
              {clubName || "Club"}
            </span>
            )}
            {showMetaSkeleton ? (
              <>
                <span
                  className="animate-pulse rounded"
                  style={{ width: 150, height: 11, background: "#EEF1F5" }}
                />
                <span
                  className="animate-pulse rounded"
                  style={{ width: 110, height: 10, background: "#F0F2F6" }}
                />
              </>
            ) : (
              <>
                {metaLine && (
                  <div
                    className="flex items-center"
                    style={{ fontSize: 12, color: "#667085", gap: 5 }}
                  >
                    {metaLine}
                  </div>
                )}
                {city && (
                  <span style={{ fontSize: 11.5, color: "#98A2B3" }}>
                    {city}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {contactPerson?.name && (
          <>
            <div style={{ height: 1, background: "#F0F2F6" }} />
            <div className="flex items-center" style={{ gap: 9 }}>
              <div
                className="flex items-center justify-center font-bold flex-shrink-0"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  background: "#EEF0FF",
                  color: "#444CE7",
                  fontSize: 11,
                }}
              >
                {String(contactPerson.name)
                  .split(/\s+/)
                  .filter(Boolean)
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
              <div className="flex flex-col min-w-0">
                <span
                  className="font-semibold truncate"
                  style={{ fontSize: 12.5 }}
                >
                  {contactPerson.name}
                </span>
                <span
                  className="truncate"
                  style={{ fontSize: 10.5, color: "#98A2B3" }}
                >
                  {contactPerson.role
                    ? `${contactPerson.role} · your contact person`
                    : "your contact person"}
                </span>
              </div>
            </div>
          </>
        )}

        {canViewProfile && (
          <button
            type="button"
            onClick={handleViewProfile}
            className="flex items-center justify-center"
            style={{
              gap: 7,
              padding: 9,
              borderRadius: 9,
              border: "1px solid #BBD7FF",
              color: "#1466D8",
              fontSize: 12.5,
              fontWeight: 600,
            }}
          >
            View club profile
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 6h8M7 3l3 3-3 3"
                stroke="#1466D8"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
    </>
  );

  // Mobile: same card content in a bottom sheet (6e "card stays" ruling).
  if (isMobile) {
    return (
      <BottomSheet open onClose={onClose} title={clubName || "Club"}>
        <div className="flex flex-col" style={{ gap: 13, paddingBottom: 8 }}>
          {renderCardBody(true)}
        </div>
      </BottomSheet>
    );
  }

  // Desktop: positioned popover.
  return (
    <>
      {/* Click-away backdrop */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={cardRef}
        role="dialog"
        aria-label={clubName ? `${clubName} club card` : "Club card"}
        className="fixed z-50 bg-white flex flex-col"
        style={{
          width: CARD_WIDTH,
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          visibility: pos ? "visible" : "hidden",
          border: "1px solid #E4E9F0",
          borderRadius: 16,
          boxShadow: "0 16px 40px rgba(16,24,40,.14)",
          padding: 18,
          gap: 13,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {renderCardBody(false)}
      </div>
    </>
  );
};

export default ClubCard;
